import { useCallback, useMemo } from "react";
import { useController, useFormContext, useFormState, useWatch } from "react-hook-form";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { MinusCircle } from "lucide-react";
import { TableCell, TableRow } from "../../../../../components/ui/table";
import CustomTimeInput from "../../../../../components/shared/inputs/CustomTimeInput";
import DurationInput, { getDurationInputValue } from "../../../../../components/shared/inputs/DurationInput";
import EditModal from "../../../../TraxxionSchedule/Component/components/drawers/Event/Form/Advanced/Section/EditModal";
import PayCodeSelect from "../../../../TraxxionSchedule/Component/components/drawers/Event/Form/components/PayCodeSelect";
import LevelController from "../../../../TraxxionSchedule/Component/components/drawers/Event/Form/Advanced/Section/LevelController";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { cn } from "../../../../../lib/utils";

function Section({ index, remove, isFirstSection, isLastSection, user, isCreating, disabled }) {
    const { t } = useTranslation();
    const { allocatedGroups } = useGroups();
    const { control, setValue, trigger } = useFormContext();

    const watchedSections = useWatch({ control, name: "sections" });
    const sections = useMemo(() => watchedSections || [], [watchedSections]);
    const shiftDate = useWatch({ control, name: "date" });
    const date = useMemo(() => moment(shiftDate).format("YYYY-MM-DD"), [shiftDate]);

    const startField = useController({
        control,
        name: `sections.${index}.start`,
        shouldUnregister: false,
    });

    const endField = useController({
        control,
        name: `sections.${index}.end`,
        shouldUnregister: false,
    });

    const payCodeField = useController({
        control,
        name: `sections.${index}.payCode`,
        shouldUnregister: false,
    });

    const durationField = useController({
        control,
        name: `sections.${index}.duration`,
        shouldUnregister: false,
    });

    const handleStartChange = useCallback((value) => {
        const endTime = sections[index]?.end;
        startField.field.onChange(value);
        if (value) {
            if (endTime) {
                const startDate = combineDateAndTime(moment(date), moment(value));
                let endDate = combineDateAndTime(moment(date), moment(endTime));

                if (moment(endDate).isBefore(moment(startDate), "seconds")) {
                    endDate = moment(endDate).add(1, "day");
                    setValue(`sections.${index}.end`, endDate);
                }

                const duration = moment(endDate).diff(moment(startDate), "seconds");
                if (duration > 0) {
                    setValue(`sections.${index}.duration`, renderDurationAsFormat(duration, "HH:mm"));
                }

                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "start",
                });
                if (isFirstSection) {
                    setValue("startDate", startDate);
                }
            }
        }
        trigger([`sections.${index}.start`, `sections.${index}.end`, `sections.${index}.duration`]);
    }, [sections, index, date, setValue, trigger, isFirstSection, startField.field]);

    const handleEndChange = useCallback((value) => {
        const startTime = sections[index]?.start;
        endField.field.onChange(value);
        if (value) {
            if (startTime) {
                const startDate = combineDateAndTime(moment(date), moment(startTime));
                let endDate = combineDateAndTime(moment(date), moment(value));
                if (moment(endDate).isBefore(moment(startDate), "seconds")) {
                    endDate = moment(endDate).add(1, "day");
                    setValue(`sections.${index}.end`, endDate);
                }

                const duration = moment(endDate).diff(moment(startDate), "seconds");
                if (duration > 0) {
                    setValue(`sections.${index}.duration`, renderDurationAsFormat(duration, "HH:mm"));
                }

                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "end",
                });
                if (isLastSection) {
                    setValue("endDate", endDate);
                }
            }
        }
        trigger([`sections.${index}.start`, `sections.${index}.end`, `sections.${index}.duration`]);
    }, [sections, index, date, setValue, trigger, isLastSection, endField.field]);

    const handleDurationChange = useCallback((value) => {
        const startTime = sections[index]?.start;
        const endTime = sections[index]?.end;
        durationField.field.onChange(value);
        if (!value) return;
        const duration = getDurationInputValue(value);
        const start = date && startTime ? combineDateAndTime(moment(date), moment(startTime)) : undefined;
        const end = date && endTime ? combineDateAndTime(moment(date), moment(endTime)) : undefined;

        if (duration > 0) {
            if (startTime) {
                let endDate = moment(start || startTime).add(duration, "seconds");
                endDate = combineDateAndTime(moment(date), moment(endDate));
                setValue(`sections.${index}.end`, endDate);
                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "end",
                });
            } else if (endTime) {
                let startDate = moment(end || endTime).subtract(duration, "seconds");
                startDate = combineDateAndTime(moment(date), moment(startDate));
                setValue(`sections.${index}.start`, startDate);
                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "start",
                });
            }
        }
        trigger([`sections.${index}.start`, `sections.${index}.end`, `sections.${index}.duration`]);
    }, [sections, index, date, setValue, trigger, durationField.field]);

    const lastBreakIndex = useMemo(() => {
        return sections?.map((section) => section.type).lastIndexOf("break")
    }, [sections]);

    const disabledWhenTypeBreak = useMemo(() => {
        const currentSection = sections?.[index];
        const calcGroupBreaks = user?.calculationGroup?.breaks;
        return !!(calcGroupBreaks?.automatic?.status &&
            currentSection?.type === "break" &&
            (sections?.[index - 1]?.type !== "break" ||
                sections?.[index + 1]?.type !== "break") &&
            sections?.[index - 1] &&
            sections?.[index + 1] &&
            isCreating);
    }, [sections, index, user, isCreating]);

    const disableStart = lastBreakIndex > -1 &&
        ((index < lastBreakIndex && index > 0) || index === lastBreakIndex + 1) &&
        isCreating;

    const disableEnd = lastBreakIndex > -1 && index < lastBreakIndex &&
        isCreating;

    const { errors } = useFormState({ control });
    const sectionErrors = errors?.sections?.[index];

    const errorClassName = "tw-border-red-500 focus-visible:tw-border-red-500 focus-visible:tw-ring-red-500/20";
    const rowErrorMessage =
        sectionErrors?.start?.message ||
        sectionErrors?.end?.message ||
        sectionErrors?.payCode?.message ||
        sectionErrors?.duration?.message ||
        (Object.values(sectionErrors || {}) as Array<{ message?: string } | undefined>)
            .find((error) => error?.message)?.message;
    const columnsCount = 4 + (allocatedGroups?.length || 0) + 1;

    return (
        <>
            <TableRow className={cn(rowErrorMessage && "tw-border-0")}>
                <TableCell className="tw-w-40">
                    <CustomTimeInput
                        placeholder={t("start")}
                        value={startField.field.value}
                        onChange={handleStartChange}
                        disabled={disabled || disabledWhenTypeBreak || disableStart}
                        className={cn(startField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>
                <TableCell className="tw-w-40">
                    <CustomTimeInput
                        placeholder={t("end")}
                        value={endField.field.value}
                        onChange={handleEndChange}
                        disabled={disabled || disabledWhenTypeBreak || disableEnd}
                        className={cn(endField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>
                <TableCell className="tw-w-44">
                    <PayCodeSelect
                        value={payCodeField.field.value}
                        onChange={payCodeField.field.onChange}
                        disabled={disabled || disabledWhenTypeBreak || disableEnd}
                        className={cn(payCodeField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>
                <TableCell className="tw-w-40">
                    <DurationInput
                        placeholder={t("duration")}
                        value={durationField.field.value}
                        disabled={disabled || disabledWhenTypeBreak || disableEnd}
                        onChange={handleDurationChange}
                        className={cn(durationField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>

                {allocatedGroups?.map((group) => (
                    <TableCell key={group.id} className="tw-w-44">
                        <LevelController
                            control={control}
                            name={`sections.${index}.${group.level.replaceAll("_", "")}`}
                            group={group}
                            disabled={disabled || disabledWhenTypeBreak || disableEnd}
                        />
                    </TableCell>
                ))}

                <TableCell className="tw-w-20">
                    <div className="tw-flex tw-gap-2">
                        {(sections?.length > 1 && !disabled) && (
                            <MinusCircle
                                className={cn(disabledWhenTypeBreak ? "tw-text-neutral-300" : "tw-text-rose-500 tw-cursor-pointer")}
                                size={16}
                                onClick={disabledWhenTypeBreak ? undefined : remove}
                            />
                        )}
                        <EditModal
                            index={index}
                            disabled={disabled || disabledWhenTypeBreak || disableEnd}
                            contentClassName="schedule-form-dialog"
                        />
                    </div>
                </TableCell>
            </TableRow>

            {rowErrorMessage && (
                <TableRow>
                    <TableCell colSpan={columnsCount} className="tw-text-rose-500 tw-pb-3">
                        {rowErrorMessage}
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

export default Section;
